// The hours a timecard pays for (legacy `Timecard::hours()`, PRESERVE per surface, T11): the clock span minus its
// unpaid and excess breaks, each break counted in whole minutes (legacy `(int) diffInMinutes`), an open break
// deducting nothing. A break's paid/unpaid kind is the snapshot it was taken with, so deleting a break definition no
// longer turns past breaks into neither (T10 FIX). An open card has no hours: payroll excludes it (CD-L16).
import { ratio } from '@radish/primitives';
import type { Instant } from '../aggregate/context.js';
import { round } from '../money/index.js';
import { f64, f64Add, f64Compare, f64Div, f64Sub, phpNumberFormatText, toF64, type F64 } from '../money/binary64.js';
import type { TimecardBreak } from './timecard/types.js';

const SECOND_MS = 1_000n;
const MINUTE_MS = 60_000n;

type Card = { readonly clockedInAt?: Instant; readonly clockedOutAt?: Instant; readonly breaks: readonly TimecardBreak[] };

/** Seconds of unpaid and excess breaks, whole minutes each. */
export function unpaidBreakSeconds(card: Card): number {
  const minutes = card.breaks
    .filter((b) => b.kind !== 'paid' && b.endedAt !== undefined)
    .reduce((total, b) => total + ((b.endedAt ?? b.startedAt) - b.startedAt) / MINUTE_MS, 0n);
  return Number(minutes * 60n);
}

/** Paid seconds of a closed card; undefined while it is open (or not yet clocked in). */
export function paidSeconds(card: Card): number | undefined {
  if (card.clockedInAt === undefined || card.clockedOutAt === undefined) return undefined;
  const span = Number((card.clockedOutAt - card.clockedInAt) / SECOND_MS);
  return Math.max(0, span - unpaidBreakSeconds(card));
}

/** Seconds as hundredths of an hour, half-up (legacy 2-dp hours, `number_format(h, 2)`): 26400 s is 733 (7.33 h). */
export const hundredthsOfHour = (seconds: number): number => Number(round(ratio(BigInt(seconds), 36n), 'HALF_UP'));

/**
 * A card's hours as legacy's timecard surfaces print them (`Timecard::hours()`, Carbon 3, in binary64): the clock span in
 * real hours, less the unpaid breaks' minutes and the excess breaks' minutes, each kind summed in fractional minutes and
 * only then truncated to whole minutes (`unpaidBreaksDurationInMinutes(): int`); an open break counts nothing. Unlike
 * legacy, a break whose definition was deleted still counts by the kind it was taken with (T10 FIX, as payroll counts it),
 * and hours never go below zero (B435). Undefined while the card is open.
 */
export function legacyCardHours(card: Card): { readonly hours: F64; readonly unpaid: F64 } | undefined {
  if (card.clockedInAt === undefined || card.clockedOutAt === undefined) return undefined;
  const minutesOf = (kind: TimecardBreak['kind']): F64 =>
    card.breaks
      .filter((b) => b.kind === kind && b.endedAt !== undefined)
      .reduce((total, b) => f64Add(total, f64Div(toF64(ratio((b.endedAt ?? b.startedAt) - b.startedAt, SECOND_MS)), f64(60n))), f64(0n));
  const wholeHours = (minutes: F64): F64 => f64Div(f64(trunc(minutes)), f64(60n));
  const unpaid = f64Add(wholeHours(minutesOf('unpaid')), wholeHours(minutesOf('excess')));
  const span = f64Div(toF64(ratio(card.clockedOutAt - card.clockedInAt, SECOND_MS)), f64(3600n));
  const hours = f64Sub(span, unpaid);
  return { hours: f64Compare(hours, f64(0n)) < 0 ? f64(0n) : hours, unpaid };
}

/** Whole part of a float, toward zero (PHP's `(int)`). */
const trunc = (x: F64): bigint => round(x, 'DOWN');

/** "1,234.56": a card's paid hours as legacy's Timecards XLSX prints them (`number_format(hours, 2)`, with its thousands comma). */
export function cardHoursText(card: Card): string | undefined {
  const h = legacyCardHours(card);
  return h === undefined ? undefined : phpNumberFormatText(h.hours, 2);
}

/**
 * "1234.56": a card's paid hours, rounded as `cardHoursText` but with no thousands separator. It is the value the
 * TimecardSummary contract carries (`hours`, /^\d+\.\d{2}$/) and what the Timecards list card and the register print,
 * where legacy prints a number rather than `number_format`'s text (the list's `paid_hours.toFixed(2)`; the register's
 * float `hours`, which legacy truncated to the thousands for a card of 1,000 hours or more, B439).
 */
export function cardHours(card: Card): string | undefined {
  const h = legacyCardHours(card);
  return h === undefined ? undefined : phpNumberFormatText(h.hours, 2, '');
}

/** "1,234.56" from the contract's bare "1234.56": legacy's `number_format` thousands comma, where legacy prints that text. */
export const hoursWithThousands = (hours: string): string => hours.replace(/^(\d+)/, (whole) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ','));

/** "0.50": a card's unpaid and excess break time as legacy prints it (`number_format(unpaid + excess hours, 2)`). */
export function cardUnpaidHoursText(card: Card): string {
  const h = legacyCardHours({ ...card, clockedInAt: card.clockedInAt ?? 0n, clockedOutAt: card.clockedOutAt ?? card.clockedInAt ?? 0n });
  return phpNumberFormatText(h?.unpaid ?? f64(0n), 2);
}
